/**
 * Privacy Policy Page Component
 */

import { memo } from 'react';

export const PrivacyPolicy = memo(function PrivacyPolicy() {
  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-white mb-8">Privacy Policy</h1>
      
      <div className="prose prose-invert max-w-none space-y-6 text-slate-300">
        <p className="text-sm text-slate-400">
          Last updated: January 22, 2026
        </p>

        <section>
          <h2 className="text-xl font-semibold text-white mb-4">Introduction</h2>
          <p>
            Welcome to ImageLab. We are committed to protecting your privacy and ensuring 
            you have a positive experience on our website. This policy outlines how we handle 
            your information when you use our free online image conversion tools.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-4">Information We Collect</h2>
          
          <h3 className="text-lg font-medium text-white mb-2">Files You Upload</h3>
          <p>
            <strong className="text-indigo-400">Your files never leave your device.</strong> All image 
            processing (SVG conversion, PNG to SVG, favicon generation, image compression, and format 
            conversion) happens entirely in your browser using JavaScript. We do not upload, store, 
            or have access to any files you process using our tools.
          </p>

          <h3 className="text-lg font-medium text-white mb-2 mt-4">Analytics Data</h3>
          <p>
            We use Google Analytics to understand how visitors interact with our website. This includes:
          </p>
          <ul className="list-disc list-inside space-y-1 text-slate-400 mt-2">
            <li>Pages visited and time spent on each page</li>
            <li>Device type, browser, and operating system</li>
            <li>Geographic location (country/city level)</li>
            <li>Referral sources (how you found our website)</li>
          </ul>
          <p className="mt-2">
            This data is anonymized and used solely to improve our services.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-4">Cookies</h2>
          <p>We use cookies for the following purposes:</p>
          <ul className="list-disc list-inside space-y-1 text-slate-400 mt-2">
            <li><strong>Analytics cookies:</strong> Google Analytics uses cookies to distinguish unique users and sessions</li>
            <li><strong>Advertising cookies:</strong> Google AdSense may use cookies to display relevant advertisements</li>
          </ul>
          <p className="mt-2">
            You can control cookies through your browser settings. Note that disabling cookies may 
            affect your experience on some websites.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-4">Third-Party Services</h2>
          
          <h3 className="text-lg font-medium text-white mb-2">Google Analytics</h3>
          <p>
            We use Google Analytics to analyze website traffic. Google may use the data collected 
            to contextualize and personalize ads on its own advertising network. You can opt out 
            of Google Analytics by installing the{' '}
            <a 
              href="https://tools.google.com/dlpage/gaoptout" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-indigo-400 hover:text-indigo-300 underline"
            >
              Google Analytics Opt-out Browser Add-on
            </a>.
          </p>

          <h3 className="text-lg font-medium text-white mb-2 mt-4">Google AdSense</h3>
          <p>
            We display advertisements through Google AdSense to support our free service. Google 
            uses cookies to serve ads based on your prior visits to our website or other websites. 
            You can opt out of personalized advertising by visiting{' '}
            <a 
              href="https://www.google.com/settings/ads" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-indigo-400 hover:text-indigo-300 underline"
            >
              Google Ads Settings
            </a>.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-4">Data Security</h2>
          <p>
            Since we don't collect or store your files, there's no risk of your images being 
            exposed through our service. All processing happens locally in your browser, and 
            your files remain on your device at all times.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-4">Children's Privacy</h2>
          <p>
            Our service is not directed to children under 13. We do not knowingly collect personal 
            information from children under 13. If you believe we have inadvertently collected 
            information from a child, please contact us so we can promptly remove it.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-4">Your Rights</h2>
          <p>Depending on your location, you may have the following rights:</p>
          <ul className="list-disc list-inside space-y-1 text-slate-400 mt-2">
            <li>Access to your personal data</li>
            <li>Correction of inaccurate data</li>
            <li>Deletion of your data</li>
            <li>Objection to data processing</li>
            <li>Data portability</li>
          </ul>
          <p className="mt-2">
            Since we don't store personal files, most of these rights apply primarily to analytics data, 
            which is managed through Google's privacy controls.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-4">Changes to This Policy</h2>
          <p>
            We may update this privacy policy from time to time. We will notify you of any changes 
            by posting the new policy on this page and updating the "Last updated" date.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-4">Contact Us</h2>
          <p>
            If you have any questions about this privacy policy or our practices, please reach out 
            to us through our GitHub repository or support channels.
          </p>
        </section>
      </div>
    </div>
  );
});
